import HeadingIcon from "@redotech/redo-icons/heading-02.svg";
import { KEYS } from "platejs";
import { useEditorRef, useEditorSelector } from "platejs/react";
import {
  type MouseEvent,
  memo,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { RedoButton } from "../../arbiter-components/buttons/redo-button";
import { RedoColorPicker } from "../../arbiter-components/color-picker/redo-color-picker";
import { RedoIcon } from "../../arbiter-components/icon/redo-icon";
import { cn } from "../../tailwind-utils";
import {
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
} from "../ui/dropdown-menu";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import {
  EDITOR_OVERLAY_Z_CLASS,
  preventEditorBlurOnMouseDown,
} from "./editor-overlay";
import { useToolbarOverflow } from "./toolbar-overflow";

export const ColorToolbarButton = memo(function ColorToolbarButton() {
  const editor = useEditorRef();
  const [open, setOpen] = useState(false);

  const selectionDefined = useEditorSelector(
    (editor) => !!editor.selection,
    [],
  );

  const color = useEditorSelector((editor) => {
    const value = editor.api.mark(KEYS.color);
    return typeof value === "string" ? value : "";
  }, []);

  const [selectedColor, setSelectedColor] = useState<string>("#000000");

  useEffect(() => {
    if (selectionDefined && color) {
      setSelectedColor(color);
    }
  }, [color, selectionDefined]);

  const handleChange = useCallback(
    (value: string) => {
      setSelectedColor(value);
      if (editor.selection) {
        editor.tf.select(editor.selection);
        editor.tf.focus();
        editor.tf.addMarks({ [KEYS.color]: value });
      }
    },
    [editor],
  );

  const ColorIcon = useMemo(
    () =>
      function ColorIcon() {
        return (
          <div className="flex flex-col items-center">
            <RedoIcon Icon={HeadingIcon} size={14} />
            <div
              className="h-0.5 w-3.5 rounded-full"
              style={{ backgroundColor: selectedColor }}
            />
          </div>
        );
      },
    [selectedColor],
  );

  const handleButtonClick = useCallback(
    (event: MouseEvent<HTMLButtonElement>) => {
      if (color && editor.selection) {
        event.stopPropagation();
        editor.tf.select(editor.selection);
        editor.tf.focus();
        editor.tf.removeMarks(KEYS.color);
      }
    },
    [color, editor],
  );

  const { isOverflow } = useToolbarOverflow();

  if (isOverflow) {
    return (
      <DropdownMenuSub>
        <DropdownMenuSubTrigger>
          <RedoIcon Icon={HeadingIcon} size={16} />
          Text color
        </DropdownMenuSubTrigger>
        <DropdownMenuSubContent
          className={cn("w-auto p-3", EDITOR_OVERLAY_Z_CLASS)}
        >
          <RedoColorPicker onChange={handleChange} value={selectedColor} />
        </DropdownMenuSubContent>
      </DropdownMenuSub>
    );
  }

  return (
    <Popover modal={false} onOpenChange={setOpen} open={open}>
      <PopoverTrigger asChild>
        <div onMouseDown={(event) => event.preventDefault()}>
          <RedoButton
            hierarchy="tertiary"
            IconLeading={ColorIcon}
            onClick={handleButtonClick}
            pressed={!!color}
            size="sm"
            text=""
          />
        </div>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className={cn("w-auto p-3", EDITOR_OVERLAY_Z_CLASS)}
        onCloseAutoFocus={(event) => event.preventDefault()}
        onMouseDown={preventEditorBlurOnMouseDown}
        onOpenAutoFocus={(event) => event.preventDefault()}
      >
        <RedoColorPicker onChange={handleChange} value={selectedColor} />
      </PopoverContent>
    </Popover>
  );
});
